"use client";
import { usePublicT } from "../../public-locale";

import { ChevronDown, Menu, Monitor, Moon, Sun, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import Link from "./MarketingLink";
import { usePathname } from "next/navigation";
import { useTheme } from "../contexts/ThemeContext";
import { useMarketingAppLinks } from "../contexts/MarketingAppLinkContext";
import Logo from "./Logo";
import IndustriesMenu from "./IndustriesMenu";
import { PublicLanguagePicker } from "../../public-locale";

const featuresLinks = [
  { href: "/reviews", label: "Reviews & Reputation" },
  { href: "/local-seo", label: "Local SEO & Visibility" },
  { href: "/ai", label: "Optrixis AI" },
  { href: "/local-score", label: "Local Score" },
  { href: "/priority-actions", label: "Priority Actions" },
  { href: "/integrations", label: "Integrations" },
];

function ThemeToggle() {
  const t = usePublicT();
  const { mode, setMode } = useTheme();
  return (
    <div
      className="flex items-center gap-0.5 rounded-full p-0.5"
      role="group"
      data-appearance-controls=""
      aria-label={t("Appearance")}
      style={{
        background: "var(--surface-2)",
        border: "1px solid var(--border)",
      }}
    >
      {t(
        (["light", "auto", "dark"] as const).map((m) => {
          const label =
            m === "auto"
              ? "Automatic appearance"
              : m === "light"
                ? "Light appearance"
                : "Dark appearance";
          return (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              aria-label={t(label)}
              className="px-2.5 py-1 rounded-full text-xs font-medium capitalize transition-all duration-200"
              style={{
                background: mode === m ? "var(--brand)" : "transparent",
                color: mode === m ? "white" : "var(--muted)",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {m === "light" ? (
                <Sun aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
              ) : m === "auto" ? (
                <Monitor aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
              ) : (
                <Moon aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
              )}
            </button>
          );
        })
      )}
    </div>
  );
}

function DropdownMenu({ id, items }: { id: string; items: { href: string; label: string }[] }) {
  const t = usePublicT();
  return (
    <div id={id} className="marketing-dropdown">
      <div
        className="rounded-2xl py-2 min-w-52"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        {t(
          items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block px-4 py-2.5 text-sm font-medium transition-colors duration-150 hover:text-brand"
              style={{
                color: "var(--foreground)",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {t(item.label)}
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

export default function Header() {
  const t = usePublicT();
  const { effective } = useTheme();
  const isDark = effective === "dark";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const focusRequest = useRef<{ id: string; last: boolean } | null>(null);
  const pathname = usePathname();
  const { signInHref } = useMarketingAppLinks();
  const isLegalPage =
    pathname === "/privacy" || pathname === "/terms" || pathname === "/data-deletion";
  const elevatedHeader = scrolled || isLegalPage || mobileOpen || dropdown !== null;

  useEffect(() => {
    setMobileOpen(false);
    setDropdown(null);
  }, [pathname]);

  useEffect(() => {
    function handleScroll() {
      const y = window.scrollY;
      setScrolled(y > 20);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen && !dropdown) return;
    function dismiss(event: PointerEvent) {
      if (event.target instanceof Node && !headerRef.current?.contains(event.target)) {
        setMobileOpen(false);
        setDropdown(null);
      }
    }
    function escape(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (mobileOpen) {
        setMobileOpen(false);
        mobileButtonRef.current?.focus();
      }
      if (dropdown) {
        if (!mobileOpen) {
          headerRef.current
            ?.querySelector<HTMLButtonElement>(`[aria-controls="${dropdown}-menu"]`)
            ?.focus();
        }
        setDropdown(null);
      }
    }
    // A blur with relatedTarget=null is not proof that focus left the header.
    // Safari/touch can emit it between pointerdown and click. Unmounting here
    // would remove the very link the browser is about to activate.
    function focusOutside(event: FocusEvent) {
      if (event.target instanceof Node && !headerRef.current?.contains(event.target)) {
        setMobileOpen(false);
        setDropdown(null);
      }
    }
    document.addEventListener("focusin", focusOutside);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("focusin", focusOutside);
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [mobileOpen, dropdown]);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const media = window.matchMedia("(min-width: 1200px)");
    function closeAtDesktop() {
      if (media.matches) setMobileOpen(false);
    }
    media.addEventListener("change", closeAtDesktop);
    return () => {
      document.body.style.overflow = previous;
      media.removeEventListener("change", closeAtDesktop);
    };
  }, [mobileOpen]);

  useLayoutEffect(() => {
    const request = focusRequest.current;
    if (!request || request.id !== dropdown) return;
    const links = headerRef.current?.querySelectorAll<HTMLAnchorElement>(`#${request.id}-menu a`);
    (request.last ? links?.[links.length - 1] : links?.[0])?.focus();
    focusRequest.current = null;
  }, [dropdown]);

  function disclosureKey(event: KeyboardEvent<HTMLButtonElement>, id: string) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault(); // Disclosure key only; anchors retain native activation.
    if (dropdown === id) {
      const links = headerRef.current?.querySelectorAll<HTMLAnchorElement>(`#${id}-menu a`);
      (event.key === "ArrowUp" ? links?.[links.length - 1] : links?.[0])?.focus();
    } else {
      focusRequest.current = { id, last: event.key === "ArrowUp" };
      setDropdown(id);
    }
  }

  return (
    <header
      ref={headerRef}
      className="marketing-site-header fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      data-legal-page={isLegalPage ? "true" : undefined}
      style={{
        background: elevatedHeader
          ? isDark
            ? "rgba(6,4,14,0.94)"
            : "rgba(250,250,249,0.94)"
          : "transparent",
        backdropFilter: elevatedHeader ? "blur(24px) saturate(180%)" : "none",
        WebkitBackdropFilter: elevatedHeader ? "blur(24px) saturate(180%)" : "none",
        borderBottom: elevatedHeader ? "1px solid var(--border)" : "1px solid transparent",
        boxShadow:
          isLegalPage || scrolled
            ? isDark
              ? "0 14px 36px rgba(0,0,0,0.18)"
              : "0 14px 36px rgba(35,20,70,0.06)"
            : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            href="/"
            className="flex items-center gap-2 flex-shrink-0"
            aria-label={t("OPTRIXIS LOCAL home")}
          >
            <Logo dark={isDark} className="h-7" />
          </Link>

          <nav
            className="marketing-desktop-nav items-center gap-1"
            aria-label={t("Primary navigation")}
          >
            <Link
              href="/platform"
              className="px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-brand"
              style={{ color: "var(--foreground)" }}
            >
              {t("Platform")}
            </Link>
            <div className="relative group px-3 py-2">
              <button
                type="button"
                className="text-sm font-medium flex items-center gap-1 transition-colors hover:text-brand"
                style={{ color: "var(--foreground)" }}
                onKeyDown={(event) => disclosureKey(event, "features")}
                aria-controls="features-menu"
                aria-expanded={dropdown === "features"}
                onClick={() => setDropdown((value) => (value === "features" ? null : "features"))}
              >
                {t("Features")}
                <ChevronDown
                  aria-hidden="true"
                  size={14}
                  strokeWidth={1.8}
                  absoluteStrokeWidth
                  className="transition-transform duration-200"
                  style={{ transform: dropdown === "features" ? "rotate(180deg)" : "none" }}
                />
              </button>
              {t(
                dropdown === "features" && <DropdownMenu id="features-menu" items={featuresLinks} />
              )}
            </div>
            <div className="relative group px-3 py-2">
              <button
                type="button"
                className="text-sm font-medium flex items-center gap-1 transition-colors hover:text-brand"
                style={{ color: "var(--foreground)" }}
                onKeyDown={(event) => disclosureKey(event, "industries")}
                aria-controls="industries-menu"
                aria-expanded={dropdown === "industries"}
                onClick={() =>
                  setDropdown((value) => (value === "industries" ? null : "industries"))
                }
              >
                {t("Industries")}
                <ChevronDown
                  aria-hidden="true"
                  size={14}
                  strokeWidth={1.8}
                  absoluteStrokeWidth
                  className="transition-transform duration-200"
                  style={{ transform: dropdown === "industries" ? "rotate(180deg)" : "none" }}
                />
              </button>
              {t(dropdown === "industries" && <IndustriesMenu id="industries-menu" />)}
            </div>
            <Link
              href="/pricing"
              className="px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-brand"
              style={{ color: "var(--foreground)" }}
            >
              {t("Pricing")}
            </Link>
            <Link
              href="/enterprise"
              className="px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-brand"
              style={{ color: "var(--foreground)" }}
            >
              {t("Enterprise")}
            </Link>
          </nav>

          <div className="marketing-desktop-nav items-center gap-3">
            <PublicLanguagePicker />
            <ThemeToggle />
            <Link
              href={signInHref}
              className="text-sm font-medium px-4 py-2 rounded-lg transition-colors hover:text-brand"
              style={{ color: "var(--muted)" }}
            >
              {t("Sign in")}
            </Link>
            <Link href="/request-access" className="btn-primary text-sm">
              {t("Request access")}
            </Link>
          </div>

          <button
            type="button"
            ref={mobileButtonRef}
            className="marketing-mobile-toggle p-2 rounded-lg"
            style={{ color: "var(--foreground)" }}
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={t(mobileOpen ? "Close navigation" : "Open navigation")}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
          >
            {mobileOpen ? (
              <X aria-hidden="true" size={22} strokeWidth={1.8} absoluteStrokeWidth />
            ) : (
              <Menu aria-hidden="true" size={22} strokeWidth={1.8} absoluteStrokeWidth />
            )}
          </button>
        </div>
      </div>

      {t(
        mobileOpen && (
          <nav
            id="mobile-navigation"
            className="marketing-mobile-nav border-t"
            aria-label={t("Mobile navigation")}
            style={{
              background: isDark ? "rgba(6,4,14,0.97)" : "rgba(250,250,249,0.97)",
              backdropFilter: "blur(24px)",
              borderColor: "var(--border)",
            }}
          >
            <div className="max-w-7xl mx-auto px-6 py-6 space-y-1">
              {t(
                [
                  { to: "/platform", label: "Platform" },
                  { to: "/reviews", label: "Reviews & Reputation" },
                  { to: "/local-seo", label: "Local SEO & Visibility" },
                  { to: "/ai", label: "Optrixis AI" },
                  { to: "/local-score", label: "Local Score" },
                  { to: "/priority-actions", label: "Priority Actions" },
                  { to: "/integrations", label: "Integrations" },
                  { to: "/pricing", label: "Pricing" },
                  { to: "/enterprise", label: "Enterprise" },
                  { to: "/about", label: "About" },
                ].map((link) => (
                  <Link
                    key={link.to}
                    href={link.to}
                    className="block py-3 text-base font-medium border-b transition-colors hover:text-brand"
                    style={{
                      color: "var(--foreground)",
                      borderColor: "var(--border)",
                    }}
                  >
                    {t(link.label)}
                  </Link>
                ))
              )}
              <button
                type="button"
                className="industry-mobile-disclosure"
                aria-controls="mobile-industries-menu"
                aria-expanded={dropdown === "industries"}
                onClick={() =>
                  setDropdown((value) => (value === "industries" ? null : "industries"))
                }
              >
                {t("Industries")}
                <ChevronDown aria-hidden="true" size={16} strokeWidth={1.8} absoluteStrokeWidth className="transition-transform duration-200" style={{ transform: dropdown === "industries" ? "rotate(180deg)" : "none" }} />
              </button>
              {dropdown === "industries" && <IndustriesMenu id="mobile-industries-menu" mobile />}
              <div className="pt-4 flex flex-col gap-3">
                <Link href={signInHref} className="btn-ghost">
                  {t("Sign in")}
                </Link>
                <PublicLanguagePicker />
                <ThemeToggle />
                <Link href="/request-access" className="btn-primary text-center">
                  {t("Request access")}
                </Link>
              </div>
            </div>
          </nav>
        )
      )}
    </header>
  );
}
